<script lang="ts">
import type { ThemeSlots } from '#layers/ui/types/tv';
import type { FormError, FormFieldInjectedOptions } from '#ui/types';
import { tv } from 'tailwind-variants';
import { injectFormContext } from './Form.vue';

const formField = tv({
	slots: {
		root: 'col-span-full',
		inlineWrapper: 'grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1',
		labelWrapper: 'group relative inline-flex w-fit items-center',
		dirtyIndicator: 'absolute -inset-s-6 top-1/2 flex -translate-y-1/2 p-2.5',
		dirtyIndicatorDot: 'size-1.5 rounded-full bg-primary transition duration-75 ease-out motion-reduce:transition-none',
		dirtyIndicatorHitArea: 'absolute inset-0',
		label: 'font-medium text-default',
		description: 'text-muted',
		error: 'font-medium text-error',
		help: 'text-xs text-muted',
		labelColumn: '',
		controlColumn: '*:w-full',
		controlWrapper: '@container flex h-full flex-col gap-1 *:w-full',
		stackedWrapper: 'flex flex-col gap-2',
		stackedControl: '*:w-full',
		inlineControl: 'w-full',
		inlineDescription: 'col-start-2',
		inlineHelp: 'col-start-2',
		indentIcon: 'size-4 shrink-0 text-muted',
	},
	variants: {
		indented: {
			true: {
				labelColumn: 'ps-6',
				stackedWrapper: 'ps-6',
				inlineWrapper: 'ps-6',
				indentIcon: '-ms-6 me-2',
			},
		},
		layout: {
			'stacked': {},
			'side-by-side': {},
			'inline': {},
			'responsive': {},
		},
		layoutSplit: {
			'1-2': {},
			'1-1': {},
		},
		centerLabel: {
			true: {
				labelColumn: 'flex flex-col justify-center',
				controlColumn: 'flex flex-col justify-center',
			},
		},
		dirty: {
			true: {
				dirtyIndicatorDot: 'scale-100 opacity-100',
			},
			false: {
				dirtyIndicator: 'pointer-events-none',
				dirtyIndicatorDot: 'scale-75 opacity-0',
			},
		},
		size: {
			sm: {
				label: 'text-xs',
				description: 'text-2xs',
				error: 'text-2xs',
			},
			md: {
				label: 'text-sm',
				description: 'text-xs',
				error: 'text-xs',
			},
			lg: {
				label: 'text-sm',
				description: 'text-sm',
				error: 'text-sm',
			},
		},
	},
	compoundVariants: [
		{
			layout: 'side-by-side',
			layoutSplit: '1-2',
			class: {
				labelColumn: 'col-span-4',
				controlColumn: 'col-span-8',
			},
		},
		{
			layout: 'responsive',
			layoutSplit: '1-2',
			class: {
				labelColumn: 'col-span-full! @lg:col-span-4!',
				controlColumn: 'col-span-full! @lg:col-span-8!',
			},
		},
		{
			layout: 'responsive',
			layoutSplit: '1-1',
			class: {
				labelColumn: 'col-span-full! @lg:col-span-6!',
				controlColumn: 'col-span-full! @lg:col-span-6!',
			},
		},
		{
			layout: 'responsive',
			indented: true,
			class: {
				labelColumn: 'ps-0! @lg:ps-6!',
				indentIcon: 'hidden @lg:block',
			},
		},
	],
	defaultVariants: {
		size: 'md',
		dirty: false,
	},
});

export interface FormFieldProps {
	/** The name of the FormField. Also used to match form errors. */
	name?: string;

	label?: string;
	description?: string;
	help?: string;
	error?: string | boolean;
	errorPattern?: RegExp;
	hint?: string;

	size?: 'sm' | 'md' | 'lg';

	required?: boolean;

	/** Whether the field's value differs from the one the form was loaded with. */
	dirty?: boolean;

	/** If true, validation on input will be active immediately instead of waiting for a blur event. */
	eagerValidation?: boolean;
	/**
	 * Delay in milliseconds before validating the form on input events.
	 * @defaultValue `300`
	 */
	validateOnInputDelay?: number;

	layout?: 'side-by-side' | 'stacked' | 'inline' | 'responsive';
	layoutSplit?: '1-2' | '1-1';

	/**
	 * Vertically center the label in the `side-by-side` layout.
	 */
	centerLabel?: boolean;

	hideLabel?: boolean;

	/**
	 * Present the field as derived from the one above it: indents the label and description, and
	 * prefixes the label with a decorative corner icon.
	 */
	indented?: boolean;

	class?: any;
	ui?: ThemeSlots<typeof formField>;
}

export interface FormFieldSlots {
	'label': (props: { label?: string }) => any;
	'label-trailing': () => any;
	'hint': (props: { hint?: string }) => any;
	'description': (props: { description?: string }) => any;
	'help': (props: { help?: string }) => any;
	'error': (props: { error?: string | boolean }) => any;
	'default': (props: { error?: string | boolean }) => any;
	'content-top': () => any;
	'content-bottom': () => any;
}
</script>

<script setup lang="ts">
import type { ComputedRef, Ref } from 'vue';
import type { FormContext } from './Form.vue';
import { createReusableTemplate } from '@vueuse/core';
import { Label, VisuallyHidden } from 'reka-ui';
import { computed, inject, provide, ref, useId, watch } from 'vue';
import { useTranslationUtils } from '#layers/i18n/public';
import { mergeUi } from '#layers/ui/utils/merge-ui';
import { formErrorsInjectionKey, formFieldInjectionKey, formInputsInjectionKey, inputIdInjectionKey } from '#ui/composables/useFormField.js';

const props = defineProps<FormFieldProps>();
const slots = defineSlots<FormFieldSlots>();

const formContext = injectFormContext(null);
// eslint-disable-next-line vue/no-dupe-keys
const { layout, layoutSplit, size } = useFormContext();
const ui = computed(() => mergeUi(props.ui, formContext?.fieldUi.value));

const theme = computed(() =>
	formField({
		size: size.value,
		indented: props.indented,
		layout: layout.value,
		layoutSplit: layoutSplit.value,
		centerLabel: props.centerLabel,
		dirty: props.dirty,
	}),
);

const { translateString } = useTranslationUtils();

const formErrors = inject<Ref<FormError[]> | null>(formErrorsInjectionKey, null);

const error = computed(() => {
	const err = props.error || formErrors?.value?.find((error) => error.name && (error.name === props.name || (props.errorPattern && error.name.match(props.errorPattern))))?.message;
	if (typeof err === 'string') return translateString(err);
	return err;
});

const id = ref(useId());
// Copies id's initial value to bind aria-attributes such as aria-describedby.
// This is required for the RadioGroup component which unsets the id value.
const ariaId = id.value;

// eslint-disable-next-line unicorn/no-useless-undefined -- Vue requires a second argument to mark injection as optional
const formInputs = inject(formInputsInjectionKey, undefined);

watch(id, () => {
	if (formInputs && props.name) {
		formInputs.value[props.name] = { id: id.value, pattern: props.errorPattern };
	}
}, { immediate: true });

provide(inputIdInjectionKey, id);

provide(formFieldInjectionKey, computed(() => ({
	error: error.value,
	name: props.name,
	size: size.value,
	eagerValidation: props.eagerValidation,
	validateOnInputDelay: props.validateOnInputDelay,
	errorPattern: props.errorPattern,
	hint: props.hint,
	description: props.description,
	help: props.help,
	ariaId,
}) as FormFieldInjectedOptions<FormFieldProps>));

const [DefineLabel, ReuseLabel] = createReusableTemplate();
const [DefineLabelImpl, ReuseLabelImpl] = createReusableTemplate();
const [DefineDescription, ReuseDescription] = createReusableTemplate();
const [DefineControl, ReuseControl] = createReusableTemplate();
const [DefineHelp, ReuseHelp] = createReusableTemplate();

function useFormContext() {
	const formContext = injectFormContext(null);

	return computedFromPropsOrContext(props, 'layout', 'layoutSplit', 'size');

	function computedFromPropsOrContext<K extends keyof FormContext & keyof FormFieldProps>(
		props: FormFieldProps,
		...keys: K[]
	): { [P in K]: ComputedRef<FormFieldProps[P]> } {
		return keys.reduce((acc, key) => {
			acc[key] = computed(() => props[key] ?? formContext?.[key]?.value as FormFieldProps[K]);
			return acc;
		}, {} as { [P in K]: ComputedRef<FormFieldProps[P]> });
	}
}
</script>

<template>
	<DefineLabelImpl>
		<div
			v-if="label || !!slots.label || !!slots['label-trailing']"
			:class="theme.labelWrapper({ class: [props.ui?.labelWrapper] })"
		>
			<DIcon
				v-if="indented"
				name="lucide:corner-down-right"
				:class="theme.indentIcon({ class: ui?.indentIcon })"
				aria-hidden="true"
			/>
			<!-- Must precede the label: consumers target the trailing slot with `label + div` -->
			<span
				data-testid="form-field-dirty"
				:class="theme.dirtyIndicator({ class: ui?.dirtyIndicator })"
			>
				<span :class="theme.dirtyIndicatorDot({ class: ui?.dirtyIndicatorDot })" />
				<template v-if="dirty">
					<DTooltip :text="$t('common.unsaved_changes')">
						<span :class="theme.dirtyIndicatorHitArea({ class: ui?.dirtyIndicatorHitArea })" />
					</DTooltip>
					<VisuallyHidden>{{ $t('common.unsaved_changes') }}</VisuallyHidden>
				</template>
			</span>
			<Label v-if="label || !!slots.label" :for="id" :class="theme.label({ class: ui?.label })">
				<slot name="label" :label>
					<DFormFieldLabel v-if="label" :label :required />
				</slot>
			</Label>
			<!-- Trailing slot for actions/buttons that should NOT be inside label -->
			<slot name="label-trailing" />
		</div>
	</DefineLabelImpl>

	<DefineLabel>
		<VisuallyHidden v-if="hideLabel" as-child>
			<ReuseLabelImpl />
		</VisuallyHidden>
		<ReuseLabelImpl v-else />
	</DefineLabel>

	<DefineDescription>
		<p
			v-if="description || !!slots.description"
			:id="`${ariaId}-description`"
			:class="theme.description({ class: ui?.description })"
		>
			<slot name="description" :description>
				{{ description }}
			</slot>
		</p>
	</DefineDescription>

	<DefineControl>
		<div :class="theme.controlWrapper({ class: ui?.controlWrapper })">
			<slot :error />
			<p
				v-if="(typeof error === 'string' && error) || !!slots.error"
				:id="`${ariaId}-error`"
				:class="theme.error({ class: ui?.error })"
			>
				<slot name="error" :error>
					{{ error }}
				</slot>
			</p>
		</div>
	</DefineControl>

	<DefineHelp>
		<p
			v-if="help || !!slots.help"
			:id="`${ariaId}-help`"
			:class="theme.help({ class: ui?.help })"
		>
			<slot name="help" :help>
				{{ help }}
			</slot>
		</p>
	</DefineHelp>

	<DGrid
		:class="theme.root({ class: [ui?.root, props.class] })"
		v-bind="$attrs"
	>
		<template v-if="layout === 'side-by-side' || layout === 'responsive'">
			<DGrid :class="theme.labelColumn({ class: ui?.labelColumn })">
				<ReuseLabel />
				<ReuseDescription />
			</DGrid>
			<DGrid :class="theme.controlColumn({ class: ui?.controlColumn })">
				<ReuseControl />
				<ReuseHelp />
			</DGrid>
		</template>
		<template v-else-if="layout === 'inline'">
			<div :class="theme.inlineWrapper({ class: ui?.inlineWrapper })">
				<ReuseLabel />
				<ReuseControl :class="theme.inlineControl({ class: ui?.inlineControl })" />
				<ReuseDescription :class="theme.inlineDescription({ class: ui?.inlineDescription })" />
				<ReuseHelp :class="theme.inlineHelp({ class: ui?.inlineHelp })" />
			</div>
		</template>
		<div v-else :class="theme.stackedWrapper({ class: ui?.stackedWrapper })">
			<slot name="content-top" />
			<ReuseLabel />
			<div :class="theme.stackedControl({ class: ui?.stackedControl })">
				<ReuseControl />
			</div>
			<ReuseDescription />
			<ReuseHelp />
			<slot name="content-bottom" />
		</div>
	</DGrid>
</template>
